import React from "react";

import { useDeviceType } from "@lululemon/ecom-site/src/util/with-device-characteristics";
import { getUsrLocaleStandard } from "@lululemon/ecom-common/src/util/locale-utils";

import WhyWeMadeThis from "./why-we-made-this";
import WhyWeMadeThisSimple from "../why-we-made-this-simple/why-we-made-this-simple";
import { PRODUCT_TIERS } from "../../constants/pcmConstants";
import { usePDPRefs } from "../../context/pdp-refs.context";
import { usePDPState } from "../../context/PDPRoot.context";

import {
  useDesignedForIcon,
  useDesignedForText,
  useLegacyProductEducation,
  useLoyaltyTermsAndConditions,
  useMaterialAndCare,
  useProductId,
  useProductTier,
  useShowEeAirSupport,
  useWhyWeMadeThisCopy,
  useWWMTImages,
} from "../../hooks/use-product-attributes";
import useHeaderHeight from "../../hooks/use-header-height";
import { lapelGetABFlag } from "../../utils/common";

/**
 * This Adapter supplies props to the underlying WhyWeMadeThis presentational component, so that it
 * no longer depepnds on the PDP container for props
 *
 * @returns {React.Component} \<WhyWeMadeThis \/\> presentational Component with all props supplied
 *
 * @example
 *  <WhyWeMadeThisAdapter />
 */
export default function WhyWeMadeThisAdapter() {
  const { currentBreakpoint, deviceType } = useDeviceType();
  const locale = getUsrLocaleStandard();
  const { wwmtRef, productDetailsRef } = usePDPRefs();
  const tier = useProductTier();
  const designedForText = useDesignedForText();
  const loyaltyTermsAndConditions = useLoyaltyTermsAndConditions();
  const materialAndCare = useMaterialAndCare();
  const productEducation = useLegacyProductEducation();
  const productId = useProductId();
  const showEeAirSupport = useShowEeAirSupport();
  const whyWeMadeThisCopy = useWhyWeMadeThisCopy();
  const {
    pdpState: {
      activeProductEducationAccordionLinkIdx,
      prodEduAccordionLinkHasBeenToggled,
    },
  } = usePDPState();
  const designedForIcon = useDesignedForIcon();
  const headerBannerHeight = useHeaderHeight();
  const wwmtImages = useWWMTImages();
  const hasAccordionImprovements = lapelGetABFlag("pdpAccordions");

  const { TIER_1, TIER_4 } = PRODUCT_TIERS;
  const { url: termsUrl, text: termsLinkText } = loyaltyTermsAndConditions;

  return tier === TIER_4 ? (
    <WhyWeMadeThisSimple
      currentBreakpoint={currentBreakpoint}
      designedFor={designedForText}
      deviceType={deviceType}
      locale={locale}
      materialAndCare={materialAndCare}
      productEducation={productEducation}
      productId={productId}
      showEeAirSupport={showEeAirSupport}
      termsUrl={termsUrl}
      termsLinkText={termsLinkText}
      whyWeMadeThis={whyWeMadeThisCopy}
      wwmtRef={wwmtRef}
    />
  ) : tier === TIER_1 ? (
    <WhyWeMadeThis
      activeProductEducationAccordionLinkIdx={
        activeProductEducationAccordionLinkIdx
      }
      currentBreakpoint={currentBreakpoint}
      designedFor={designedForText}
      designedForIcon={designedForIcon}
      deviceType={deviceType}
      headerBannerHeight={headerBannerHeight}
      locale={locale}
      termsUrl={termsUrl}
      termsLinkText={termsLinkText}
      materialAndCare={materialAndCare}
      primaryImage={wwmtImages?.primaryImage}
      prodEduAccordionLinkHasBeenToggled={prodEduAccordionLinkHasBeenToggled}
      productDetailsRef={productDetailsRef}
      productEducation={productEducation}
      secondaryImage={wwmtImages?.secondaryImage}
      showEeAirSupport={showEeAirSupport}
      whyWeMadeThis={whyWeMadeThisCopy}
      wwmtRef={wwmtRef}
      productId={productId}
      hasAccordionImprovements={hasAccordionImprovements}
    />
  ) : null;
}

WhyWeMadeThisAdapter.displayName = "WhyWeMadeThisAdapter";
